// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback } from 'react';

import { useEvolvePaginatedGet } from '@evolvemep/api-ts';
import { Stack, Flex, Text } from '@mantine/core';

import { isNotNil } from 'helpers/isNotNil';
import { TriggerOnVisible } from 'hooks/useOnScreen';
import { EditableWorkOrderItem } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/EditableWorkOrderItem';
import {
  type AddWorkOrderItemsFromCatalogModalProps,
  AddWorkOrderItemsFromCatalogModal,
} from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/FromCatalog/AddWorkOrderItemsFromCatalogModal';
import { AddWorkOrderWriteInItemButton } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/FromCatalog/AddWorkOrderWriteInItemButton';

type Props = Pick<
  AddWorkOrderItemsFromCatalogModalProps,
  'opened' | 'onClose' | 'workOrder' | 'parentWorkOrderItem' | 'onItemsChanged'
>;

export const AddItemsToWorkOrderFromCatalogModal = ({ onItemsChanged: onItemsChangedParent, ...props }: Props) => {
  const { workOrder, parentWorkOrderItem } = props;

  const {
    data: existingItems,
    fetchNextPage: fetchNextPageItems,
    loading: loadingItems,
    reset: refreshItems,
  } = useEvolvePaginatedGet('shop/workOrder/item', {
    defaultConfig: {
      params: {
        workOrderId: workOrder.workOrderId,
      },
    },
  });

  const onItemsChanged = useCallback<typeof onItemsChangedParent>(() => {
    refreshItems();
    onItemsChangedParent();
  }, [refreshItems, onItemsChangedParent]);

  return (
    <AddWorkOrderItemsFromCatalogModal {...props} onItemsChanged={onItemsChanged}>
      {({ onDeleteItem }) => (
        <>
          <Stack gap={0}>
            <Flex justify="space-between" align="center">
              <Text fw="bold">{isNotNil(parentWorkOrderItem) ? <>Bill of materials</> : <>Work order items</>}</Text>
              <AddWorkOrderWriteInItemButton
                workOrder={workOrder}
                parentWorkOrderItem={parentWorkOrderItem}
                onItemAdded={onItemsChanged}
              />
            </Flex>
          </Stack>
          <Stack flex={1} style={{ overflowY: 'scroll' }} pr="xs">
            <Stack flex="0 0 auto">
              {existingItems.map((item) => (
                <EditableWorkOrderItem
                  key={item.workOrderItemId}
                  item={item}
                  workOrder={workOrder}
                  hideDetails
                  onItemUpdated={onItemsChanged}
                  onMultipleItemsUpdated={onItemsChanged}
                  onDeleteWorkOrderItem={onDeleteItem}
                />
              ))}
              <TriggerOnVisible onVisible={fetchNextPageItems} loading={loadingItems} />
            </Stack>
          </Stack>
        </>
      )}
    </AddWorkOrderItemsFromCatalogModal>
  );
};
